import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw, Clock, BarChart3, LineChart as LineChartIcon, ArrowRightLeft } from 'lucide-react';
import { createChart, CandlestickSeries, AreaSeries, CrosshairMode } from 'lightweight-charts';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TROY_OZ_TO_GRAMS = 31.1035;

function WeightConverter() {
  const [oz, setOz] = useState('1');
  const [grams, setGrams] = useState((1 * TROY_OZ_TO_GRAMS).toFixed(4));
  const [kg, setKg] = useState((1 * TROY_OZ_TO_GRAMS / 1000).toFixed(6));

  const handleOz = (v) => {
    setOz(v);
    const n = parseFloat(v) || 0;
    setGrams((n * TROY_OZ_TO_GRAMS).toFixed(4));
    setKg((n * TROY_OZ_TO_GRAMS / 1000).toFixed(6));
  };
  const handleGrams = (v) => {
    setGrams(v);
    const n = parseFloat(v) || 0;
    setOz((n / TROY_OZ_TO_GRAMS).toFixed(6));
    setKg((n / 1000).toFixed(6));
  };
  const handleKg = (v) => {
    setKg(v);
    const n = parseFloat(v) || 0;
    setGrams((n * 1000).toFixed(4));
    setOz((n * 1000 / TROY_OZ_TO_GRAMS).toFixed(6));
  };

  const presets = [
    { label: '1 oz', val: 1 },
    { label: '1/2 oz', val: 0.5 },
    { label: '1/4 oz', val: 0.25 },
    { label: '1/10 oz', val: 0.1 },
    { label: '10 oz', val: 10 },
    { label: '1 kg', val: 32.1507 },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Troy Ounces (oz t)</label>
          <input
            type="number"
            step="any"
            value={oz}
            onChange={e => handleOz(e.target.value)}
            className="w-full h-11 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] bg-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            data-testid="converter-oz"
          />
        </div>
        <div className="hidden sm:flex items-center justify-center">
          <ArrowRightLeft size={18} className="text-[#D4AF37]" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Grams (g)</label>
          <input
            type="number"
            step="any"
            value={grams}
            onChange={e => handleGrams(e.target.value)}
            className="w-full h-11 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] bg-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            data-testid="converter-grams"
          />
        </div>
      </div>
      <div className="mt-3">
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Kilograms (kg)</label>
          <input
            type="number"
            step="any"
            value={kg}
            onChange={e => handleKg(e.target.value)}
            className="w-full sm:w-1/3 h-11 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] bg-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            data-testid="converter-kg"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {presets.map(p => (
          <button
            key={p.label}
            onClick={() => handleOz(String(p.val))}
            className="text-xs px-3 py-1.5 rounded-sm border border-gray-200 text-gray-600 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            data-testid={`preset-${p.label.replace(/[\s\/]/g, '-')}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 mt-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
        1 Troy Ounce = {TROY_OZ_TO_GRAMS} grams. Troy ounces are the standard unit for precious metals trading.
      </p>
    </div>
  );
}

function PriceChangeIndicator({ change, changePct }) {
  if (!change && !changePct) return <span className="text-xs text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>--</span>;
  const isUp = change > 0;
  const isDown = change < 0;
  const color = isUp ? 'text-emerald-500' : isDown ? 'text-red-500' : 'text-gray-400';
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Icon size={12} />
      {isUp ? '+' : ''}{changePct?.toFixed(2)}%
    </span>
  );
}

function TradingViewChart({ data, chartType, selectedMetal, height = 400 }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || data.length === 0) return;

    // Clean up previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#FFFFFF' },
        textColor: '#6B7280',
        fontFamily: "'Poppins', sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#F3F4F6' },
        horzLines: { color: '#F3F4F6' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#D4AF37', width: 1, style: 2, labelBackgroundColor: '#D4AF37' },
        horzLine: { color: '#D4AF37', width: 1, style: 2, labelBackgroundColor: '#D4AF37' },
      },
      rightPriceScale: {
        borderColor: '#E5E7EB',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: '#E5E7EB',
        timeVisible: false,
        rightOffset: 5,
        barSpacing: chartType === 'candle' ? 8 : 4,
        tickMarkFormatter: (time) => {
          if (typeof time === 'string') {
            const d = new Date(time + 'T00:00:00');
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}`;
          }
          return '';
        },
      },
      localization: {
        priceFormatter: (price) => `NZ$${price.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}`,
        timeFormatter: (time) => {
          if (typeof time === 'string') {
            const d = new Date(time + 'T00:00:00');
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
          }
          return '';
        },
      },
    });

    chartRef.current = chart;

    if (chartType === 'candle') {
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#16A34A',
        downColor: '#DC2626',
        borderUpColor: '#16A34A',
        borderDownColor: '#DC2626',
        wickUpColor: '#16A34A',
        wickDownColor: '#DC2626',
      });
      candleSeries.setData(data);
    } else {
      const metalColors = { gold: '#D4AF37', silver: '#94A3B8', platinum: '#818CF8', palladium: '#A78BFA', copper: '#B87333' };
      const color = metalColors[selectedMetal] || '#D4AF37';
      const areaSeries = chart.addSeries(AreaSeries, {
        lineColor: color,
        lineWidth: 2,
        topColor: `${color}33`,
        bottomColor: `${color}05`,
        crosshairMarkerRadius: 4,
        crosshairMarkerBorderColor: '#FFFFFF',
        crosshairMarkerBorderWidth: 2,
        crosshairMarkerBackgroundColor: color,
      });
      // Convert line data: {date, price} -> {time, value}
      const lineData = data.map(d => ({
        time: d.time || d.date,
        value: d.close || d.price,
      }));
      areaSeries.setData(lineData);
    }

    chart.timeScale().fitContent();

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [data, chartType, selectedMetal, height]);

  return <div ref={chartContainerRef} data-testid={`trading-chart-${selectedMetal}`} />;
}

function MetalChartSection({ metal, prices, periods }) {
  const [period, setPeriod] = useState('3m');
  const [chartType, setChartType] = useState('candle');
  const [ohlcData, setOhlcData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const label = metal.charAt(0).toUpperCase() + metal.slice(1);

  useEffect(() => {
    setChartLoading(true);
    Promise.all([
      axios.get(`${API}/prices/ohlc`, { params: { metal, period, interval: 'daily' } }),
      axios.get(`${API}/prices/history`, { params: { metal, period } }),
    ]).then(([ohlcRes, lineRes]) => {
      setOhlcData(ohlcRes.data?.candles || []);
      setLineData(lineRes.data || []);
      setChartLoading(false);
    }).catch(err => {
      console.error(`Chart data fetch failed for ${metal} ${period}:`, err);
      setChartLoading(false);
    });
  }, [metal, period]);

  const chartData = chartType === 'candle' ? ohlcData : lineData;

  const chartStats = useMemo(() => {
    if (chartType === 'candle' && ohlcData.length >= 2) {
      const closes = ohlcData.map(c => c.close);
      const highs = ohlcData.map(c => c.high);
      const lows = ohlcData.map(c => c.low);
      const current = closes[closes.length - 1];
      const start = closes[0];
      return {
        current, high: Math.max(...highs), low: Math.min(...lows),
        change: current - start, changePct: ((current - start) / start) * 100,
      };
    }
    if (lineData.length >= 2) {
      const vals = lineData.map(h => h.price);
      const current = vals[vals.length - 1];
      const start = vals[0];
      return {
        current, high: Math.max(...vals), low: Math.min(...vals),
        change: current - start, changePct: ((current - start) / start) * 100,
      };
    }
    return null;
  }, [chartType, ohlcData, lineData]);

  const spotData = prices?.changes?.[metal] || {};

  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`price-chart-${metal}`}>
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-6 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {label} Price Chart
            <span className="text-xs text-gray-400 font-normal ml-2">NZD/toz</span>
          </h2>
          {chartStats && (
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-sm font-medium ${chartStats.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                {chartStats.change >= 0 ? '+' : ''}NZ${chartStats.change.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}
                {' '}({chartStats.changePct >= 0 ? '+' : ''}{chartStats.changePct.toFixed(2)}%)
              </span>
              <span className="text-xs text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>over period</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-gray-50 p-1 rounded-sm">
            <button onClick={() => setChartType('candle')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${chartType === 'candle' ? 'bg-[#0A0A0A] text-[#D4AF37] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`chart-type-candle-${metal}`}
            ><BarChart3 size={12} /> Candle</button>
            <button onClick={() => setChartType('area')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${chartType === 'area' ? 'bg-[#0A0A0A] text-[#D4AF37] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`chart-type-area-${metal}`}
            ><LineChartIcon size={12} /> Area</button>
          </div>
          <div className="flex gap-1 bg-gray-50 p-1 rounded-sm">
            {periods.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${period === p.key ? 'bg-[#0A0A0A] text-[#D4AF37] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`period-${p.key}-${metal}`}
              >{p.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        {chartLoading ? (
          <div className="flex items-center justify-center" style={{ height: 400 }}>
            <RefreshCw size={24} className="text-gray-300 animate-spin" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center text-gray-400" style={{ height: 400, fontFamily: "'Poppins', sans-serif" }}>
            No chart data available
          </div>
        ) : (
          <TradingViewChart data={chartData} chartType={chartType} selectedMetal={metal} height={400} />
        )}
      </div>

      {/* Chart Stats */}
      {chartStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-t border-gray-100 bg-gray-50/50">
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Current</p>
            <p className="text-sm font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${chartStats.current?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Period High</p>
            <p className="text-sm font-semibold text-emerald-600" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${chartStats.high?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Period Low</p>
            <p className="text-sm font-semibold text-red-500" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${chartStats.low?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Period Change</p>
            <p className={`text-sm font-semibold ${chartStats.change >= 0 ? 'text-emerald-600' : 'text-red-500'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
              {chartStats.changePct >= 0 ? '+' : ''}{chartStats.changePct.toFixed(2)}%
            </p>
          </div>
        </div>
      )}

      {/* Live Spot Data */}
      {(spotData.bid > 0 || spotData.high > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Bid</p>
            <p className="text-sm font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${spotData.bid?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Ask</p>
            <p className="text-sm font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${spotData.ask?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Day High</p>
            <p className="text-sm font-semibold text-emerald-600" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${spotData.high?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>Day Low</p>
            <p className="text-sm font-semibold text-red-500" style={{ fontFamily: "'Poppins', sans-serif" }}>NZ${spotData.low?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LivePricesPage() {
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const metals = ['gold', 'silver', 'platinum', 'palladium', 'copper'];
  const periods = [
    { key: '1w', label: '1W' },
    { key: '1m', label: '1M' },
    { key: '3m', label: '3M' },
    { key: '6m', label: '6M' },
    { key: '1y', label: '1Y' },
  ];

  const fetchPrices = useCallback(() => {
    axios.get(`${API}/prices`).then(r => {
      setPrices(r.data);
      setLastRefresh(new Date());
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  const priceCards = useMemo(() => {
    if (!prices) return [];
    return metals.map(m => ({
      metal: m,
      price: prices[`${m}_nzd`] || 0,
      label: m.charAt(0).toUpperCase() + m.slice(1),
      change: prices.changes?.[m]?.change || 0,
      changePct: prices.changes?.[m]?.change_pct || 0,
    })).filter(c => c.price > 0);
  }, [prices]);

  const sourceLabel = prices?.source === 'metals.dev' ? 'Live' : prices?.source === 'cached' ? 'Cached' : 'Indicative';

  return (
    <div data-testid="live-prices-page">
      <SEO
        title="Live Gold & Silver Prices NZD"
        description="Live NZD spot prices for gold, silver, platinum, palladium and copper. Interactive candlestick and area charts with historical data. Troy ounce to gram converter."
        path="/live-prices"
      />
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">Market Data</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }} data-testid="live-prices-heading">
            Live Prices & Charts
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Track NZD spot prices for gold, silver, platinum, palladium, and copper.
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full ${sourceLabel === 'Live' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-gray-800 text-gray-400'}`} data-testid="price-source-badge" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <span className={`w-1.5 h-1.5 rounded-full ${sourceLabel === 'Live' ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
              {sourceLabel}
            </span>
            {lastRefresh && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <Clock size={10} />
                {lastRefresh.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 max-w-7xl mx-auto px-6 md:px-12">
        {/* Price Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="price-cards-grid">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`price-skeleton-${i}`} className="p-6 rounded-sm border-2 border-gray-200 bg-white animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-16 mb-3" />
                <div className="h-7 bg-gray-200 rounded w-32 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-20" />
              </div>
            ))
          ) : priceCards.map(card => (
            <div
              key={card.metal}
              className="p-5 md:p-6 rounded-sm border-2 border-gray-200 bg-white text-left"
              data-testid={`price-card-${card.metal}`}
            >
              <p className="text-xs uppercase tracking-wider font-semibold mb-1 text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {card.label}
              </p>
              <p className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
                NZ${card.price?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}
              </p>
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>per troy oz</p>
                <PriceChangeIndicator change={card.change} changePct={card.changePct} />
              </div>
            </div>
          ))}
        </div>

        {/* Gold Price Chart */}
        <MetalChartSection metal="gold" prices={prices} periods={periods} />

        {/* Silver Price Chart */}
        <div className="mt-8">
          <MetalChartSection metal="silver" prices={prices} periods={periods} />
        </div>

        {/* Weight Conversion Calculator */}
        <div className="mt-8 p-6 bg-white rounded-sm border border-gray-200" data-testid="weight-converter">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>Weight Converter</h3>
            <span className="text-xs text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>Troy Ounces &middot; Grams &middot; Kilograms</span>
          </div>
          <WeightConverter />
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-[#FAFAFA] rounded-sm border border-gray-100" data-testid="prices-disclaimer">
          <p className="text-sm text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <strong>Disclaimer:</strong> Prices shown are indicative NZD spot prices per troy ounce sourced from metals.dev and are updated regularly.
            Actual purchase and sell-back prices will include a premium/discount. Contact us for a live quote.
            Prices do not constitute financial advice.
            {prices?.source !== 'metals.dev' && (
              <span className="block mt-2 text-xs text-gray-400">
                Prices are currently sourced from cached/default data. Live market data requires API configuration.
              </span>
            )}
          </p>
        </div>
      </section>
    </div>
  );
}
